import './PersonalInformation.scss';

import React, { useCallback, useEffect, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Alert from 'fe-design-base/molecules/Alert/Alert';
import AutocompleteField from 'fe-design-base/molecules/AutocompleteField';
import TaxIdField from 'fe-design-base/molecules/TaxIdField';
import TextField from 'fe-design-base/molecules/TextField';
import { useFormikContext } from 'formik';

import { getPayrollUSStateOptions } from 'selectors/session';

import MaskedTextField from 'features/team/components/MaskedTextField';
import { MASK_MAP } from 'features/team/components/MaskedTextField/constants';
import {
  I18N_LABEL_PATH,
  I18N_PATH,
} from 'features/team/components/TaxEntryForm/constants';
import {
  ADD_TEAM_PAYROLL_FLOW_STEP,
  EVENT_ACTIONS,
} from 'features/teamView/tracking';

import { isInUS } from 'components/forms/TextField/util';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import { TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

import AddressConfirmAlert from './Alert/AddressConfirmAlert';
import ConfirmSuggestedAddressAlert from './Alert/ConfirmSuggestedAddressAlert';
import NoAddressAlert from './Alert/NoAddressAlert';

const { cx, cxEl } = cxHelpers('PersonalInformation');

export const PersonalInformation = ({ stateOptions, userData }) => {
  const trackUx = useTrackUx();
  useEffect(() => {
    trackUx(EVENT_ACTIONS.PAGE_VIEWED, TRACK_ACTION_TYPES.VIEW, {
      add_team_payroll_flow_step: ADD_TEAM_PAYROLL_FLOW_STEP.PERSONAL_INFO,
    });
  }, [trackUx]);

  const [showAlert, setShowAlert] = useState(true);

  const { values } = useFormikContext();

  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
  }, []);

  const businessContractor =
    userData.tax_classification === '1099' &&
    userData.contractor_type === 'business';

  const isEmployee = userData.tax_classification === 'w2';

  return (
    <Box pb={48} className={cx()}>
      {showAlert && (
        <Box mb={28}>
          <Alert
            variant="info"
            title={toI18n(`${I18N_PATH}.alert.title`)}
            onClose={handleCloseAlert}
          >
            {toI18n(`${I18N_PATH}.alert.desc`)}
          </Alert>
        </Box>
      )}

      {values.ssn_error && (
        <Box mb={28}>
          <Alert variant="error">{values.ssn_error}</Alert>
        </Box>
      )}

      <Box mb={14} className={cxEl('grid')}>
        <TextField
          autoFocus
          hasAsterisk
          name="legal_first_name"
          label={toI18n(`${I18N_LABEL_PATH}.legal_first_name`)}
        />

        <TextField
          name="legal_middle_name"
          label={toI18n(`${I18N_LABEL_PATH}.legal_middle_name`)}
        />

        <Box className={cxEl('take-two')} column>
          <TextField
            hasAsterisk
            name="legal_last_name"
            label={toI18n(`${I18N_LABEL_PATH}.legal_last_name`)}
          />
        </Box>

        <MaskedTextField
          hasAsterisk={isEmployee}
          name="date_of_birth"
          placeholder="MM/DD/YYYY"
          label={toI18n(`${I18N_LABEL_PATH}.date_of_birth`)}
          mask={MASK_MAP.date}
        />

        {businessContractor ? (
          <TaxIdField
            hasAsterisk
            name="ein"
            placeholder="## - #######"
            isEIN
            label={toI18n(`${I18N_LABEL_PATH}.ein`)}
          />
        ) : (
          <TaxIdField
            hasAsterisk
            name="ssn"
            placeholder="### - ## - ####"
            stripChars={false}
            label={toI18n(`${I18N_LABEL_PATH}.ssn`)}
          />
        )}

        {businessContractor && (
          <Box className={cxEl('take-two')} column>
            <TextField
              hasAsterisk
              name="contractor_info_business_name"
              label={toI18n(`${I18N_LABEL_PATH}.contractor_info_business_name`)}
            />
          </Box>
        )}

        <Box className={cxEl('take-two')} column>
          <TextField
            hasAsterisk={isEmployee || businessContractor}
            name="address_1"
            label={toI18n(`${I18N_LABEL_PATH}.address_1`)}
          />
        </Box>

        <Box className={cxEl('take-two')} column>
          <TextField
            name="address_2"
            label={toI18n(`${I18N_LABEL_PATH}.address_2`)}
          />
        </Box>

        <Box className={cxEl('take-two')} column>
          <TextField
            hasAsterisk={isEmployee || businessContractor}
            name="city"
            label={toI18n(`${I18N_LABEL_PATH}.city`)}
          />
        </Box>

        <Box className={cxEl('take-three')} column>
          <AutocompleteField
            hasAsterisk={isEmployee || businessContractor}
            name="state"
            label={toI18n(`${I18N_LABEL_PATH}.state`)}
            options={stateOptions.toArray()}
            showCaret
          />
        </Box>

        <Box className={cxEl('take-one')} column>
          {isInUS() ? (
            <MaskedTextField
              hasAsterisk={isEmployee || businessContractor}
              name="zip_code"
              label={toI18n(`${I18N_LABEL_PATH}.zip_code`)}
              mask={MASK_MAP.zip}
            />
          ) : (
            <TextField
              hasAsterisk={isEmployee || businessContractor}
              name="zip_code"
              label={toI18n(`${I18N_LABEL_PATH}.zip_code`)}
            />
          )}
        </Box>
      </Box>

      {/* If there is an address suggestion and error message is present */}
      {values.address_suggestion &&
        values.address_suggestion_status === 'pending' && (
          <ConfirmSuggestedAddressAlert />
        )}

      {/*
        If there is an address suggestion and error message is not present.
        Means a user has already confirmed suggested address
      */}
      {values.address_suggestion &&
        values.address_suggestion_status === 'approved' && (
          <AddressConfirmAlert />
        )}

      {/* If there is no an address suggestion and error message is present */}
      {!values.address_suggestion &&
        values.address_suggestion_status === 'pending' && <NoAddressAlert />}
    </Box>
  );
};

PersonalInformation.propTypes = {
  stateOptions: ImmutablePropTypes.list.isRequired,
};

export default connect((state, props) => ({
  stateOptions: getPayrollUSStateOptions(state, props),
}))(PersonalInformation);
